import { Link } from 'react-router-dom';
import {
  Aviso,
  Cartao,
  CartaoIndicador,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
} from '../ui';
import type { ClienteDoRecorrente, ResumoDoRecorrente } from '../../servicos/visaoGeral';
import { SeloDeContrato } from './comum';

/**
 * O que só o Recorrente tem, dentro da visão geral (D-015): era a aba própria
 * dele no Painel. Aparece com o Recorrente no filtro; segue o mesmo período.
 */
export function DetalheDoRecorrente(props: { readonly dados: ResumoDoRecorrente }): JSX.Element {
  const { dados } = props;
  return (
    <section className="flex min-w-0 flex-col gap-4">
      <h2 className="font-display text-lg font-semibold tracking-tight">Recorrente em detalhe</h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <CartaoIndicador
          rotulo="Conversão"
          valor={<ValorPercentual percentual={dados.conversaoPercentual} tamanho="grande" />}
          detalhe={`${dados.clientesComContratoFechado} de ${dados.clientes} cliente${
            dados.clientes === 1 ? '' : 's'
          } com cálculo fecharam contrato`}
        />
        <CartaoIndicador
          rotulo="Meses aproveitados"
          valor={
            <span className="valor-fiscal text-3xl font-semibold">{dados.mesesAproveitados}</span>
          }
          detalhe="Meses com a transferência confirmada pelo fornecedor"
        />
      </div>

      {/*
        A fila do período em quatro números: é a lista de trabalho da operação,
        e o que muda o dia de quem abre o painel.
      */}
      <Cartao
        como="section"
        titulo="O que a fila está devendo"
        subtitulo="Envios do cronograma agendados para dentro do período, das empresas do recorte."
      >
        <div className="grid gap-3 sm:grid-cols-4">
          <Pendencia
            rotulo="Vencidos"
            valor={dados.fila.vencidos}
            tom="erro"
            ajuda="Passaram da data e não saíram"
          />
          <Pendencia
            rotulo="Falharam"
            valor={dados.fila.falharam}
            tom="erro"
            ajuda="Erro no envio — conferir contatos"
          />
          <Pendencia
            rotulo="Aguardando etapa"
            valor={dados.fila.aguardandoEtapaAnterior}
            tom="atencao"
            ajuda="Esperam certificado da etapa anterior"
          />
          <Pendencia
            rotulo="Agendados"
            valor={dados.fila.agendados}
            tom="neutro"
            ajuda="Na fila, saem na data"
          />
        </div>
      </Cartao>

      {dados.linhas.length > 0 && (
        <Tabela
          legenda="Clientes do Recorrente no recorte"
          legendaVisivel
          densidade="compacta"
          colunas={COLUNAS}
          dados={dados.linhas}
          chaveDaLinha={(l) => l.clienteId}
          chaveDeCabecalhoDaLinha="cliente"
          alturaMaxima="32rem"
        />
      )}

      {dados.clientes > 0 && dados.mesesAproveitados === 0 && (
        <Aviso tom="informacao" titulo="Meses aproveitados ainda em zero">
          A contagem sobe quando o fornecedor confirma a transferência do mês — pelo link do e-mail
          ou com o certificado anexado no cronograma.
        </Aviso>
      )}
    </section>
  );
}

const COLUNAS: readonly ColunaTabela<ClienteDoRecorrente>[] = [
  {
    chave: 'cliente',
    titulo: 'Cliente',
    larguraMinima: '15rem',
    celula: (l) => (
      <span className="flex flex-col">
        <Link
          to={`/clientes/${l.clienteId}`}
          className="text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
        >
          {l.clienteNome}
        </Link>
        <span className="text-xs text-conteudo-sutil">
          {l.mesesComSped} mês{l.mesesComSped === 1 ? '' : 'es'} apurado
          {l.mesesComSped === 1 ? '' : 's'}
        </span>
      </span>
    ),
  },
  {
    chave: 'status',
    titulo: 'Contrato',
    larguraMinima: '9rem',
    celula: (l) => <SeloDeContrato status={l.situacao === 'SEM_CONTRATO' ? null : l.situacao} />,
  },
  {
    chave: 'assessor',
    titulo: 'Assessor',
    larguraMinima: '11rem',
    celula: (l) =>
      l.assessorNome === null ? (
        <span className="text-xs text-conteudo-sutil">operadora</span>
      ) : (
        <span className="text-sm text-conteudo-secundario">{l.assessorNome}</span>
      ),
  },
  {
    chave: 'fornecedor',
    titulo: 'Fornecedor',
    larguraMinima: '12rem',
    celula: (l) =>
      l.fornecedorNome === null ? (
        <span className="text-xs text-conteudo-sutil">a definir</span>
      ) : (
        <span className="text-sm text-conteudo-secundario">{l.fornecedorNome}</span>
      ),
  },
  {
    chave: 'credito',
    titulo: 'Crédito utilizado',
    numerica: true,
    larguraMinima: '9rem',
    celula: (l) => <ValorFiscal valor={l.creditoNegociado} tamanho="pequeno" />,
  },
  {
    chave: 'desagio',
    titulo: 'Deságio',
    numerica: true,
    larguraMinima: '7rem',
    celula: (l) => <ValorPercentual percentual={l.desagioMedioPercentual} tamanho="pequeno" />,
  },
  {
    chave: 'valorPago',
    titulo: 'A pagar',
    numerica: true,
    larguraMinima: '9rem',
    celula: (l) => <ValorFiscal valor={l.valorDosClientes} tamanho="pequeno" />,
  },
  {
    chave: 'beneficio',
    titulo: 'Benefício',
    numerica: true,
    larguraMinima: '9rem',
    celula: (l) => (
      <ValorFiscal valor={l.beneficioEconomico} variante="economia" tamanho="pequeno" />
    ),
  },
  {
    chave: 'aproveitados',
    titulo: 'Aproveitados',
    numerica: true,
    larguraMinima: '7rem',
    celula: (l) => <span className="valor-fiscal text-sm">{l.mesesAproveitados}</span>,
  },
];

function Pendencia(props: {
  readonly rotulo: string;
  readonly valor: number;
  readonly tom: 'erro' | 'atencao' | 'neutro';
  readonly ajuda: string;
}): JSX.Element {
  const cor =
    props.valor === 0
      ? 'text-conteudo-sutil'
      : props.tom === 'erro'
        ? 'text-erro'
        : props.tom === 'atencao'
          ? 'text-atencao'
          : 'text-conteudo-primario';

  return (
    <div className="flex flex-col gap-1 rounded-card border border-borda px-4 py-3">
      <span className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</span>
      <span className={`valor-fiscal font-display text-2xl font-semibold ${cor}`}>
        {props.valor}
      </span>
      <span className="text-xs text-conteudo-sutil">{props.ajuda}</span>
    </div>
  );
}
